<template>
    <b-tab title-link-class="p-0" v-if="isOwner(soapDetails.business_id)">
        <template v-slot:title>
            <b-nav-item-dropdown no-flip name="tab-items">
                <template slot="button-content">
                    <i class="cus-page-white-stack"></i>
                    Records
                </template>
                <loader v-if="_.isEmpty(stats)" />
                <template v-else>
                    <dropdown-nested
                        :title="{
                            text:
                                'View Records (' + stats.all_record_count + ')',
                            icon: 'cus-page-white-stack'
                        }"
                        :list="recordsChildDropdown"
                        :isHideArrowWhenListEmpty="true"
                        @trigger-function="recordAction"
                    />
                    <dropdown-nested
                        :title="{
                            text:
                                'View Diagnostics (' +
                                stats.diagnostic_count +
                                ')',
                            icon: 'cus-chart-curve'
                        }"
                        :list="diagnosticsChildDropdown"
                        :isHideArrowWhenListEmpty="true"
                        @trigger-function="diagnosticAction"
                    />
                    <dropdown-nested
                        :title="{
                            text: 'View Vitals (' + stats.vital_count + ')',
                            icon: 'cus-heart'
                        }"
                        :list="vitalsChildDropdown"
                        :isHideArrowWhenListEmpty="true"
                        @trigger-function="vitalAction"
                    />
                    <b-dropdown-item
                        v-if="isRelatedToTreatment"
                        @click="currentView = TREATMENT"
                    >
                        <i class="cus-stethoscope"></i>
                        View Activities ({{ stats.all_activity_count }})
                    </b-dropdown-item>
                </template>
                <!-- Based on apps/application/views/staff/patient/soap/sub/navbar.php -->
                <template
                    v-if="
                        !statusInActive && _hasAccess('patient.record.create')
                    "
                >
                    <li class="dropdown-divider"></li>

                    <b-dropdown-item
                        v-if="
                            useRelatedToTreatment &&
                            _hasAccess('schedule.treatmentboard.create')
                        "
                        @click.stop.prevent="
                            openModal(HISTORY.VIEWS.ACTIVITIES, true)
                        "
                    >
                        <i class="cus-add"></i>
                        New Bundle
                    </b-dropdown-item>
                    <b-dropdown-item
                        v-else
                        @click.stop.prevent="
                            openModal(HISTORY.VIEWS.RECORDS_BUNDLE)
                        "
                    >
                        <i class="cus-add"></i>
                        New Bundle
                    </b-dropdown-item>
                    <b-dropdown-item
                        @click.stop.prevent="openModal(HISTORY.VIEWS.RECORDS)"
                    >
                        <i class="cus-add"></i>
                        New Record
                    </b-dropdown-item>
                    <b-dropdown-item
                        @click.stop.prevent="
                            openModal(HISTORY.VIEWS.DIAGNOSTIC)
                        "
                    >
                        <i class="cus-add"></i>
                        New Diagnostic
                    </b-dropdown-item>
                    <b-dropdown-item
                        @click.stop.prevent="openModal(HISTORY.VIEWS.VITALS)"
                    >
                        <i class="cus-add"></i>
                        New Vital
                    </b-dropdown-item>
                    <b-dropdown-item
                        v-if="
                            useRelatedToTreatment &&
                            _hasAccess('schedule.treatmentboard.create')
                        "
                        @click.stop.prevent="
                            openModal(HISTORY.VIEWS.ACTIVITIES)
                        "
                    >
                        <i class="cus-add"></i>
                        New Activity
                    </b-dropdown-item>
                </template>
            </b-nav-item-dropdown>
        </template>
        <b-card-text id="tab-record-content">
            <ListRecord
                v-if="currentView === HISTORY.VIEWS.RECORDS"
                :key="keyview"
                :category="currentCategory"
                :patientDetails="soapDetails.patient"
                v-on="$listeners"
            />
            <ListDiagnostic
                v-if="currentView === HISTORY.VIEWS.DIAGNOSTIC"
                :key="keyview"
                :category="currentCategory"
                :patientId="soapDetails.patient.id"
                :soapId="soapDetails.id"
            />
            <ListVital
                v-if="currentView === HISTORY.VIEWS.VITALS && soapDetails"
                :key="keyview"
                :category="currentCategory"
                :patientId="soapDetails.patient.id"
                :soapId="soapDetails.id"
            />
            <ListActivities
                v-if="currentView === TREATMENT && isRelatedToTreatment"
                :key="keyview"
                :treatmentId="soapDetails.treatment_board_id"
                :dialogs="dialogs"
                v-on="$listeners"
            />
        </b-card-text>
    </b-tab>
</template>

<script>
/**
 * @name SoapTabRecords
 * @see apps/application/views/staff/patient/soap/sub/navbar.php
 * @see apps/application/controllers/patient/history.php
 * @see apps/application/views/staff/patient/soap/sub/record/list.php
 * @see apps/application/views/staff/patient/soap/sub/vital/list.php
 */

import HISTORY from "@/constants/history";
import CVITAL from "@/constants/vital";
import ScheduleHelper from "@/module/schedule/helper";
import { forEach, sortBy, concat, isEmpty } from "lodash";

export default {
    mixins: [ScheduleHelper],
    components: {
        Loader: () => import("@/components/elements/loader"),
        DropdownNested: () =>
            import("@/components/elements/dropdown/dropdown-nested"),
        ListRecord: () => import("@/components/medical-records/record-list"),
        ListVital: () => import("@/components/vitals/vital-list"),
        ListDiagnostic: () =>
            import("@/components/patient-diagnostic/diagnostic-list"),
        ListActivities: () =>
            import("@/components/schedule-treatment/activity-list.vue")
    },
    props: {
        dialogs: {
            type: Object,
            default() {
                return {};
            }
        }
    },
    data() {
        return {
            keyview: 0,
            recordsChildDropdown: [],
            diagnosticsChildDropdown: [],
            vitalsChildDropdown: [],
            currentView: "",
            currentCategory: null,
            HISTORY,
            TREATMENT: "treatment"
        };
    },
    created() {
        if (isEmpty(this.diagnosticList)) {
            this.$store.dispatch("references/getDiagnostics");
        }
    },
    computed: {
        soapDetails() {
            return this.$store.getters["soap/soapDetails"];
        },
        stats() {
            let stats = this.$store.getters["soap/countsSoap"];

            this.updateRecordsStats(stats);
            this.updateDiagnosticsStats(stats);
            this.updateVitalStats(stats);
            return stats;
        },
        diagnosticList() {
            return this.$store.getters["references/diagnostics"];
        },
        statusInActive() {
            return this.$store.getters["soap/patientStatusInActive"];
        },
        isRelatedToTreatment() {
            return this.soapDetails?.treatment_board_id;
        },
        useRelatedToTreatment() {
            return (
                this.soapDetails?.treatment_status_id ==
                this.TREATMENT_STATUS.TREATMENT
            );
        }
    },
    methods: {
        showList(view) {
            switch (view) {
                case HISTORY.VIEWS.RECORDS:
                    this.recordAction();
                    break;
                case HISTORY.VIEWS.DIAGNOSTIC:
                    this.diagnosticAction();
                    break;
                case HISTORY.VIEWS.VITALS:
                    this.vitalAction();
                    break;
                case HISTORY.VIEWS.ACTIVITIES:
                    if (
                        [
                            this.TREATMENT,
                            HISTORY.VIEWS.VITALS,
                            HISTORY.VIEWS.RECORDS
                        ].includes(this.currentView)
                    ) {
                        this.keyview++;
                    } else {
                        this.currentView = this.TREATMENT;
                        this.keyview++;
                    }
                    break;
            }
            this.$emit("scroll-to-element", "tab-record-content");
        },
        recordAction(type) {
            this.currentCategory = null;
            if (type !== "ALL") {
                this.currentCategory = type;
            }
            this.currentView = HISTORY.VIEWS.RECORDS;
            this.keyview++;
        },
        diagnosticAction(type) {
            this.currentCategory = null;
            if (type !== "ALL") {
                this.currentCategory = type;
            }
            this.currentView = HISTORY.VIEWS.DIAGNOSTIC;
            this.keyview++;
        },
        vitalAction(type) {
            this.currentCategory = null;

            if (type !== "VITAL_ALL") {
                this.currentCategory = type;
            }

            this.currentView = HISTORY.VIEWS.VITALS;
            this.keyview++;
        },
        updateRecordsStats(stats) {
            this.recordsChildDropdown = [];

            if (stats) {
                // record related
                if (stats.all_record_count > 0) {
                    this.recordsChildDropdown.push({
                        text: "All Records (" + stats.all_record_count + ")",
                        icon: "cus-page-white-stack",
                        function_code: "ALL"
                    });

                    this.recordsChildDropdown.push({ icon: "divider" });
                }

                if (stats.record_type_count) {
                    let list = [];
                    forEach(stats.record_type_count, function (value, key) {
                        if (value.count > 0) {
                            list.push({
                                text: value.name + " (" + value.count + ")",
                                icon: value.icon_class,
                                function_code: key
                            });
                        }
                    });

                    let sortedList = sortBy(list, ["text"]);

                    this.recordsChildDropdown = concat(
                        this.recordsChildDropdown,
                        sortedList
                    );
                }
            }
        },
        updateDiagnosticsStats(stats) {
            this.diagnosticsChildDropdown = [];

            if (stats) {
                // diagnostic related
                let self = this;
                if (stats.diagnostic_count > 0) {
                    this.diagnosticsChildDropdown.push({
                        text:
                            "All Diagnostics (" + stats.diagnostic_count + ")",
                        icon: "cus-chart-curve",
                        function_code: "ALL"
                    });

                    this.diagnosticsChildDropdown.push({ icon: "divider" });
                    if (this.diagnosticList) {
                        forEach(this.diagnosticList, function (diagnostic) {
                            let diagnosticStat =
                                stats.all_vital_count[diagnostic.id];
                            if (diagnosticStat > 0) {
                                self.diagnosticsChildDropdown.push({
                                    text:
                                        diagnostic.vital +
                                        " (" +
                                        diagnosticStat +
                                        ")",
                                    icon: "",
                                    function_code: diagnostic.id
                                });
                            }
                        });
                    }
                }
            }
        },
        updateVitalStats(stats) {
            this.vitalsChildDropdown = [];

            if (stats) {
                if (stats.vital_count > 0) {
                    this.vitalsChildDropdown.push({
                        text: "All Vitals (" + stats.vital_count + ")",
                        icon: "cus-heart",
                        function_code: "VITAL_ALL"
                    });

                    this.vitalsChildDropdown.push({ icon: "divider" });

                    if (stats.all_vital_count) {
                        if (stats.all_vital_count[CVITAL.TYPE.CAPILLARY] > 0) {
                            this.vitalsChildDropdown.push({
                                text:
                                    "Capillary Refill Time (" +
                                    stats.all_vital_count[
                                        CVITAL.TYPE.CAPILLARY
                                    ] +
                                    ")",
                                function_code: CVITAL.TYPE.CAPILLARY, //6
                                icon: ""
                            });
                        }

                        if (stats.all_vital_count[CVITAL.TYPE.HEARTRATE] > 0) {
                            this.vitalsChildDropdown.push({
                                text:
                                    "Heart Rate (" +
                                    stats.all_vital_count[
                                        CVITAL.TYPE.HEARTRATE
                                    ] +
                                    ")",
                                function_code: CVITAL.TYPE.HEARTRATE, //1,
                                icon: ""
                            });
                        }

                        if (
                            stats.all_vital_count[CVITAL.TYPE.RESPIRATION] > 0
                        ) {
                            this.vitalsChildDropdown.push({
                                text:
                                    "Respiration (" +
                                    stats.all_vital_count[
                                        CVITAL.TYPE.RESPIRATION
                                    ] +
                                    ")",
                                function_code: CVITAL.TYPE.RESPIRATION, //2,
                                icon: ""
                            });
                        }

                        if (
                            stats.all_vital_count[CVITAL.TYPE.TEMPERATURE] > 0
                        ) {
                            this.vitalsChildDropdown.push({
                                text:
                                    "Temperature (" +
                                    stats.all_vital_count[
                                        CVITAL.TYPE.TEMPERATURE
                                    ] +
                                    ")",
                                function_code: CVITAL.TYPE.TEMPERATURE, //3,
                                icon: ""
                            });
                        }

                        if (stats.all_vital_count[CVITAL.TYPE.WEIGHT] > 0) {
                            this.vitalsChildDropdown.push({
                                text:
                                    "Weight (" +
                                    stats.all_vital_count[CVITAL.TYPE.WEIGHT] +
                                    ")",
                                function_code: CVITAL.TYPE.WEIGHT, //4,
                                icon: ""
                            });
                        }
                    }
                }
            }
        },
        openModal(key, isTreatmentBundle = false) {
            this.$emit("open-modal", key, isTreatmentBundle);
        }
    }
};
</script>
